import React, { useState, useMemo, useEffect } from 'react';
import { Search, Filter, Target, Dumbbell, Clock, Eye, Plus, Loader2, AlertCircle } from 'lucide-react';
import { Exercise } from '../types/dataModel';
import { exerciseCategories, muscleGroups, equipmentTypes, difficultyLevels } from '../data/exercises';
import ExerciseDetail from '../components/ExerciseDetail';
import CreateExerciseForm from '../components/CreateExerciseForm';

import { useAuth } from '../contexts/AuthContext';
import { useTrainerLegacyExerciseRedirect } from '../features/trainer/shell/useTrainerLegacyExerciseRedirect';
import { ExerciseService } from '../lib/exerciseService';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { PAGE_CHROME_LIGHT, PAGE_CHROME_LOADER_BRAND_GREEN } from '../constants/pageChrome';
import { usePageStatusBar } from '../hooks/usePageStatusBar';

export const Exercises = () => {
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedMuscleGroup, setSelectedMuscleGroup] = useState('all');
  const [selectedEquipment, setSelectedEquipment] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState<Exercise | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const { user, profile } = useAuth();
  const redirectingTrainer = useTrainerLegacyExerciseRedirect();

  usePageStatusBar({
    background: loading ? PAGE_CHROME_LOADER_BRAND_GREEN : PAGE_CHROME_LIGHT,
  });

  // Fetch exercises from database
  useEffect(() => {
    if (redirectingTrainer) return;
    const fetchExercises = async () => {
      try {
        setLoading(true);
        setError(null);
        const exercisesData = await ExerciseService.getExercises();
        setExercises(exercisesData);
      } catch (err) {
        console.error('Error fetching exercises:', err);
        setError('Failed to load exercises. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchExercises();
  }, [redirectingTrainer]);

  // Block scroll when modal is open
  useEffect(() => {
    if (selectedExercise || showCreateForm) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [selectedExercise, showCreateForm]);



  // Filter exercises based on selected criteria
  const filteredExercises = useMemo(() => {
    if (!exercises) return [];
    
    return exercises.filter(exercise => {
      const matchesSearch = exercise.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (exercise.description || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      // NEW SCHEMA: Only filter by search and muscleGroup (single, not array)
      const matchesMuscleGroup = selectedMuscleGroup === 'all' || 
                                (exercise.muscleGroup || '').toLowerCase() === selectedMuscleGroup.toLowerCase();
      
      return matchesSearch && matchesMuscleGroup;
    });
  }, [exercises, searchTerm, selectedCategory, selectedMuscleGroup, selectedEquipment, selectedDifficulty]);

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'text-green-600 bg-green-100';
      case 'intermediate': return 'text-yellow-600 bg-yellow-100';
      case 'advanced': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'strength': return '💪';
      case 'cardio': return '❤️';
      case 'flexibility': return '🧘';
      case 'balance': return '⚖️';
      case 'sports': return '🏃';
      default: return '🏃';
    }
  };

  const resetFilters = () => {
    setSelectedCategory('all');
    setSelectedMuscleGroup('all');
    setSelectedEquipment('all');
    setSelectedDifficulty('all');
    setSearchTerm('');
  };

  const handleExerciseClick = (exercise: Exercise) => {
    // Always show modal (full-page on mobile, overlay on desktop)
    setSelectedExercise(exercise);
  };

  const handleCreateExercise = async (exerciseData: any) => {
    setIsCreating(true);
    try {
      const newExercise = await ExerciseService.createExercise(exerciseData);
      console.log('Exercise created successfully:', newExercise);
      
      // Refresh the exercises list
      const updatedExercises = await ExerciseService.getExercises();
      setExercises(updatedExercises);
      
      setShowCreateForm(false);
    } catch (error) {
      console.error('Error creating exercise:', error);
      setError('Failed to create exercise. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  // Loading state
  if (redirectingTrainer) {
    return <LoadingSpinner fullScreen />;
  }

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen py-12 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Exercises</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Exercise Library
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-6">
            Discover exercises to build your perfect workout routine. 
            Filter by category, muscle group, equipment, and difficulty level.
          </p>
          {/* Create Exercise Button for Trainers */}
          {profile?.role === 'trainer' && (
            <button
              onClick={() => setShowCreateForm(true)}
              className="btn-primary inline-flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Create Custom Exercise
            </button>
          )}
        </div>

        {/* Search and Filter Bar */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search Input */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search exercises..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="lg:hidden px-4 py-2 border border-gray-300 rounded-lg flex items-center gap-2 hover:bg-gray-50 transition-colors"
            >
              <Filter className="w-5 h-5" />
              Filters
            </button>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg lg:hidden">
              <div className="grid grid-cols-2 gap-4">
                {/* Category Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="all">All Categories</option>
                    {exerciseCategories.filter(cat => cat.id !== 'all').map(category => (
                      <option key={category.id} value={category.id}>
                        {category.icon} {category.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Difficulty Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Difficulty</label>
                  <select
                    value={selectedDifficulty}
                    onChange={(e) => setSelectedDifficulty(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="all">All Levels</option>
                    {difficultyLevels.filter(level => level.id !== 'all').map(level => (
                      <option key={level.id} value={level.id}>
                        {level.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Muscle Group Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Muscle Group</label>
                  <select
                    value={selectedMuscleGroup}
                    onChange={(e) => setSelectedMuscleGroup(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="all">All Muscle Groups</option>
                    {muscleGroups.map(muscleGroup => (
                      <option key={muscleGroup} value={muscleGroup}>
                        {muscleGroup}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Equipment Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Equipment</label>
                  <select
                    value={selectedEquipment}
                    onChange={(e) => setSelectedEquipment(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="all">All Equipment</option>
                    {equipmentTypes.map(equipment => (
                      <option key={equipment} value={equipment}>
                        {equipment}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Reset Filters Button */}
              <button
                onClick={resetFilters}
                className="mt-4 w-full px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
              >
                Reset Filters
              </button>
            </div>
          )}

          {/* Desktop Filters */}
          <div className="hidden lg:grid lg:grid-cols-4 gap-4 mt-4">
            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Categories</option>
                {exerciseCategories.filter(cat => cat.id !== 'all').map(category => (
                  <option key={category.id} value={category.id}>
                    {category.icon} {category.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Difficulty Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Difficulty</label>
              <select
                value={selectedDifficulty}
                onChange={(e) => setSelectedDifficulty(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Levels</option>
                {difficultyLevels.filter(level => level.id !== 'all').map(level => (
                  <option key={level.id} value={level.id}>
                    {level.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Muscle Group Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Muscle Group</label>
              <select
                value={selectedMuscleGroup}
                onChange={(e) => setSelectedMuscleGroup(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Muscle Groups</option>
                {muscleGroups.map(muscleGroup => (
                  <option key={muscleGroup} value={muscleGroup}>
                    {muscleGroup}
                  </option>
                ))}
              </select>
            </div>

            {/* Equipment Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Equipment</label>
              <select
                value={selectedEquipment}
                onChange={(e) => setSelectedEquipment(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Equipment</option>
                {equipmentTypes.map(equipment => (
                  <option key={equipment} value={equipment}>
                    {equipment}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Results Count and Reset */}
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-gray-600">
              Showing {filteredExercises.length} of {exercises.length} exercises
            </p>
            <button
              onClick={resetFilters}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Reset Filters
            </button>
          </div>
        </div>

        {/* Exercises Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredExercises.map((exercise) => (
            <div key={exercise.id} className="card hover:shadow-xl transition-shadow duration-300">

              
              {/* Exercise Header */}
              <div className="mb-3">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                    {exercise.name}
                  </h3>
                </div>
                <p className="text-gray-600 text-sm line-clamp-2">
                  {exercise.description || 'No description available'}
                </p>
              </div>

              {/* Exercise Details */}
              {exercise.muscleGroup && (
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Target className="w-4 h-4" />
                    <span className="line-clamp-1">
                      {exercise.muscleGroup}
                    </span>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2">
                <button 
                  onClick={() => handleExerciseClick(exercise)}
                  className="flex-1 btn-primary text-sm py-2"
                >
                  View Details
                </button>
                <button className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors">
                  <Eye className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* No Results */}
        {filteredExercises.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No exercises found</h3>
            <p className="text-gray-600 mb-4">
              Try adjusting your search terms or filters to find what you're looking for.
            </p>
            <button
              onClick={resetFilters}
              className="btn-primary"
            >
              Reset Filters
            </button>
          </div>
        )}
      </div>

      {/* Exercise Detail Modal */}
      {selectedExercise && (
        <ExerciseDetail
          exercise={selectedExercise}
          onClose={() => setSelectedExercise(null)}
        />
      )}

      {/* Create Exercise Form Modal */}
      {showCreateForm && (
        <CreateExerciseForm
          onSubmit={handleCreateExercise}
          onCancel={() => setShowCreateForm(false)}
          isLoading={isCreating}
        />
      )}
    </div>
  );
};

export default Exercises;
